import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { required } from 'redux-form-validators';
import { Field, reduxForm } from 'redux-form';
import { compose } from 'redux';

import { addUserBackofficeDocuments } from 'backoffice/users/_redux';

import { withTheme } from 'common/styling/theme';
import { DefaultButton, BasicModal, ReduxSelectField, FileField } from 'common/components';

import { staticStyles, getDynamicStyles } from './style';

const docLimit = 10;

const UserDocumentsAddForm = ({
  handleSubmit,
  submitting,
  error,
  dispatch,
  isOpen,
  intl,
  theme,
  userId,
  passport,
  addressProof,
  identificationProof,
  passportWithFace,
  additionalDocument,
  onClose,
  addUserBackofficeDocuments,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [moreLimit, setMoreLimit] = useState(false);

  const submit = handleSubmit(({ documentType }) =>
    addUserBackofficeDocuments({ documentType, files: uploadedDocs, userId }, dispatch)
  );

  const documentsOptions = [
    ...(passport && passport.enable
      ? [{ value: 'passport', label: intl.formatMessage({ id: 'copyOfPassportWithResidAddress' }) }]
      : []),
    ...(passportWithFace && passportWithFace.enable
      ? [{ value: 'passport_with_face', label: intl.formatMessage({ id: 'proofOfLife' }) }]
      : []),
    ...(addressProof && addressProof.enable
      ? [{ value: 'address_proof', label: intl.formatMessage({ id: 'proofResidAddress' }) }]
      : []),
    ...(identificationProof && identificationProof.enable
      ? [{ value: 'identification_proof', label: intl.formatMessage({ id: 'proofOfIdent' }) }]
      : []),
    ...(additionalDocument && additionalDocument.enable
      ? [{ value: 'additional_document', label: intl.formatMessage({ id: 'additionalDocumentCaption' }) }]
      : []),
  ];

  const acceptedFormats = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/msword', // doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  ];

  const handleUpload = files => {
    if (files.length > docLimit) {
      setMoreLimit(true);
    } else {
      setMoreLimit(false);
      setUploadedDocs(files);
    }
  };

  return (
    <BasicModal captionId="justAddDocument" isOpen={isOpen} onRequestClose={onClose}>
      <form className="UserDocumentsAddForm" onSubmit={submit}>
        <Field
          component={ReduxSelectField}
          name="documentType"
          textId="justDocumentType"
          options={documentsOptions}
          validate={[required()]}
          disabled={submitting}
        />
        <div
          className={classNames('UserDocumentsAddForm__fileField', {
            'UserDocumentsAddForm__fileField--error': moreLimit,
          })}>
          <Field
            component={FileField}
            handleAfterUpload={handleUpload}
            name="document"
            formatNameId="documentsFormats"
            fileNameId="justFiles"
            accept={acceptedFormats.join(',')}
            validate={[required()]}
            multiple
          />
        </div>

        {error && <strong className="formError">{error}</strong>}
        {moreLimit && (
          <span className="UserDocumentsAddForm__error">
            <FormattedMessage id="clientMaxDocError" />
          </span>
        )}

        <DefaultButton
          textId="justSubmit"
          type="submit"
          disabled={submitting || error || moreLimit}
          loading={submitting}
          filled
        />
      </form>
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </BasicModal>
  );
};

UserDocumentsAddForm.propTypes = {
  submitting: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addUserBackofficeDocuments: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  passport: PropTypes.object.isRequired,
  identificationProof: PropTypes.object.isRequired,
  passportWithFace: PropTypes.object.isRequired,
  addressProof: PropTypes.object.isRequired,
  additionalDocument: PropTypes.object.isRequired,
  error: PropTypes.string,
  theme: PropTypes.object,
};

UserDocumentsAddForm.defaultProps = {
  error: '',
  theme: {},
};

export default compose(
  injectIntl,
  withTheme(),
  connect(
    state => ({
      passport: state.interfaceConfig.kycSettings.passport,
      identificationProof: state.interfaceConfig.kycSettings.identification_proof,
      addressProof: state.interfaceConfig.kycSettings.address_proof,
      passportWithFace: state.interfaceConfig.kycSettings.passport_with_face,
      additionalDocument: state.interfaceConfig.kycSettings.additional_document,
    }),
    { addUserBackofficeDocuments: data => addUserBackofficeDocuments.request(data) }
  ),
  reduxForm({
    form: 'user-documents-add-form',
    onSubmitSuccess: (result, dispatch, props) => {
      props.onClose();
    },
  })
)(UserDocumentsAddForm);
export { UserDocumentsAddForm };
